import { useLanguage } from '@/contexts/language-context';
import { formatMoney } from '@/lib/utils';
import { formatDate } from '@/routes/jobs/helpers';
import type { SharedInvoiceDto } from '@/lib/api/public-invoices';

import { formatDateTime } from '@/routes/invoices/helpers';

interface Props {
  invoice: SharedInvoiceDto;
}

function statusLabel(
  status: SharedInvoiceDto['status'],
  t: ReturnType<typeof useLanguage>['t'],
): string {
  switch (status) {
    case 'paid':
      return t.invoiceStatusPaid;
    case 'pending_confirmation':
      return t.invoiceStatusPendingConfirmation;
    default:
      return t.invoiceStatusUnpaid;
  }
}

function statusColorClass(status: SharedInvoiceDto['status']): string {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'pending_confirmation':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    default:
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }
}

// Read-only, invoice-style rendering of the sanitized public payload. Mirrors
// the look of `routes/public-quote/shared-quote-view.tsx`.
export function SharedInvoiceView({ invoice }: Props) {
  const { t } = useLanguage();

  const meta: string[] = [];
  if (invoice.sent_at) meta.push(`${t.sharedInvoiceSent} ${formatDateTime(invoice.sent_at)}`);
  if (invoice.due_date) meta.push(`${t.invoiceDueDate} ${formatDate(invoice.due_date)}`);
  meta.push(`${t.sharedInvoiceExpires} ${formatDateTime(invoice.expires_at)}`);

  return (
    <div
      data-testid="shared-invoice-view"
      className="rounded-lg border bg-background p-5 flex flex-col gap-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {invoice.logo_url && (
            <img
              src={invoice.logo_url}
              alt={t.sharedDocumentLogoAlt}
              data-testid="shared-invoice-logo"
              className="w-12 h-12 rounded-lg object-contain border bg-background shrink-0"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-tight">{invoice.company_name}</h1>
            <p className="text-sm text-muted-foreground">
              {t.sharedInvoiceFor} {invoice.customer_name}
            </p>
            {invoice.job_name && (
              <p className="text-sm text-muted-foreground truncate">{invoice.job_name}</p>
            )}
          </div>
        </div>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${statusColorClass(invoice.status)}`}
          data-testid="shared-invoice-status"
        >
          {statusLabel(invoice.status, t)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground -mt-3">{meta.join(' · ')}</p>

      {/* Amounts */}
      <div className="flex flex-col gap-1 text-sm">
        {invoice.subtotal != null && (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{t.invoiceSubtotal}</span>
            <span className="tabular-nums">{formatMoney(invoice.subtotal)}</span>
          </div>
        )}
        {invoice.tax != null && (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{t.invoiceTax}</span>
            <span className="tabular-nums">{formatMoney(invoice.tax)}</span>
          </div>
        )}
        {invoice.discount != null && invoice.discount !== 0 && (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{t.invoiceDiscount}</span>
            <span className="tabular-nums">-{formatMoney(invoice.discount)}</span>
          </div>
        )}
        <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
          <span>{t.invoiceTotal}</span>
          <span className="tabular-nums" data-testid="shared-invoice-total">
            {formatMoney(invoice.total_amount)}
          </span>
        </div>
      </div>
    </div>
  );
}
